/**
 * Zeitachse eines Tages oder Zeitraums: Karte und Verlauf nebeneinander.
 *
 * Das Datum steht in der Adresszeile, nicht im Komponentenzustand — so ist eine
 * Ansicht teilbar und der Zurück-Knopf tut, was man erwartet.
 *
 * Mit `bis` in der Adresse zeigt die Seite einen Zeitraum statt eines Tages.
 */

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CalendarDays,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  Footprints,
  MapPin,
  Pencil,
  Radio,
} from "lucide-react";

import {
  api,
  ApiFehler,
  zuFremdspuren,
  type FremdSpur,
  type PositionEintrag,
  type QuelleInfo,
  type Tag,
  type Verlaufseintrag,
} from "@/api.ts";
import { SegmentBearbeiten } from "@/komponenten/SegmentBearbeiten.tsx";
import { GOOGLE, QuellenAuswahl } from "@/komponenten/QuellenAuswahl.tsx";
import { ortungAnzeige, ortungAus } from "@/ortung.ts";
import { googleMapsUrl } from "@/karten.ts";
import {
  heuteLokal,
  ortszeitFallsAbweichend,
  tagUndUhrzeitLokal,
  uhrzeitLokal,
  versatzText,
} from "@/zeit.ts";

const Kartenansicht = lazy(() =>
  import("@/komponenten/Karte.tsx").then((m) => ({ default: m.Kartenansicht })),
);

/** Eine Zeile der verwobenen Liste: entweder ein Google-Segment oder ein Fremdpunkt. */
type Listenzeile =
  | { typ: "segment"; zeit: number; eintrag: Verlaufseintrag }
  | { typ: "position"; zeit: number; farbe: string; pos: PositionEintrag };

const heute = heuteLokal;

export function TagSeite() {
  const [felder, setFelder] = useSearchParams();
  const datum = felder.get("datum") ?? heute();
  const bis = felder.get("bis") ?? "";
  const istZeitraum = bis !== "" && bis > datum;

  // Die gewählten Quellen stehen in der Adresszeile. Fehlt der Parameter (null),
  // gilt allein die Google-Zeitachse — das bisherige Verhalten. Ein leerer
  // Parameter (`?quellen=`) heißt dagegen „alles abgewählt", nicht „Default";
  // deshalb wird auf null geprüft, nicht auf Wahrheitswert.
  const quellenParam = felder.get("quellen");
  const gewaehlteQuellen = useMemo(
    () => new Set(quellenParam === null ? [GOOGLE] : quellenParam.split(",").filter(Boolean)),
    [quellenParam],
  );
  const zeigtGoogle = gewaehlteQuellen.has(GOOGLE);

  const [tag, setTag] = useState<Tag | null>(null);
  const [fremdspuren, setFremdspuren] = useState<FremdSpur[]>([]);
  const [quellen, setQuellen] = useState<QuelleInfo[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gewaehlt, setGewaehlt] = useState<number | null>(null);
  const [zentrum, setZentrum] = useState<[number, number] | null>(null);
  const [bearbeitung, setBearbeitung] = useState<number | null>(null);
  const listeRef = useRef<HTMLElement>(null);

  // Die Quellenliste einmal holen — für das Auswahlmenü.
  useEffect(() => {
    api.quellen().then(setQuellen).catch(() => setQuellen([]));
  }, []);

  useEffect(() => {
    let verworfen = false;
    setLaedt(true);
    setFehler(null);
    setGewaehlt(null);
    setZentrum(null);
    setBearbeitung(null);

    const fremdIds = [...gewaehlteQuellen].filter((id) => id !== GOOGLE);
    const vonIso = `${datum}T00:00:00`;
    const bisIso = `${istZeitraum ? bis : datum}T23:59:59.999`;

    const googleP: Promise<Tag> = zeigtGoogle
      ? istZeitraum
        ? api.zeitraum(datum, bis)
        : api.tag(datum)
      : Promise.resolve({ datum, verlauf: [], spur: [] });

    const fremdP: Promise<FremdSpur[]> =
      fremdIds.length > 0
        ? api
            .positionen(vonIso, bisIso)
            .then((alle) => zuFremdspuren(alle.filter((p) => fremdIds.includes(p.quelleId))))
        : Promise.resolve([]);

    Promise.all([googleP, fremdP])
      .then(([g, f]) => {
        if (verworfen) return;
        setTag(g);
        setFremdspuren(f);
      })
      .catch((e: unknown) => {
        if (verworfen) return;
        setFehler(e instanceof ApiFehler ? e.message : String(e));
        setTag(null);
        setFremdspuren([]);
      })
      .finally(() => {
        if (!verworfen) setLaedt(false);
      });

    return () => {
      verworfen = true;
    };
  }, [datum, bis, istZeitraum, quellenParam, zeigtGoogle, gewaehlteQuellen]);

  const setzeFelder = useCallback(
    (werte: Record<string, string>) => {
      const neu: Record<string, string> = { datum: werte.datum ?? datum };
      if (werte.bis) neu.bis = werte.bis;
      // Die Quellenauswahl beim Blättern und Datumswechsel erhalten.
      if (quellenParam !== null) neu.quellen = quellenParam;
      setFelder(neu, { replace: false });
    },
    [datum, quellenParam, setFelder],
  );

  const setzeQuellen = useCallback(
    (neu: Set<string>) => {
      const werte: Record<string, string> = { datum };
      if (istZeitraum) werte.bis = bis;
      const liste = [...neu];
      const nurGoogle = liste.length === 1 && liste[0] === GOOGLE;
      // Nur-Google ist der Default → Parameter weglassen (saubere URL). Leere
      // Auswahl wird als `quellen=` festgehalten, damit sie nicht zum Default
      // zurückfällt.
      if (!nurGoogle) werte.quellen = liste.join(",");
      setFelder(werte, { replace: false });
    },
    [datum, bis, istZeitraum, setFelder],
  );

  const waehlen = useCallback(
    (id: number) => {
      if (bearbeitung !== null) return;
      setZentrum(null);
      setGewaehlt((alt) => (alt === id ? null : id));
    },
    [bearbeitung],
  );

  const fremdpunktWaehlen = useCallback((p: PositionEintrag) => {
    setGewaehlt(null);
    // Neues Array-Objekt, damit der Zentrier-Effekt auch bei gleicher Koordinate feuert.
    setZentrum([p.lon, p.lat]);
  }, []);

  const segmentAktualisiert = useCallback(
    (aktualisiert: Verlaufseintrag) => {
      setTag((alt) => {
        if (!alt) return alt;
        return {
          ...alt,
          verlauf: alt.verlauf.map((e) => (e.id === aktualisiert.id ? aktualisiert : e)),
        };
      });
      setBearbeitung(null);
    },
    [],
  );

  useEffect(() => {
    if (gewaehlt == null || !listeRef.current) return;
    const zeile = listeRef.current.querySelector(`[data-id="${gewaehlt}"]`);
    zeile?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [gewaehlt]);

  const verschieben = (tage: number) => {
    const d = new Date(`${datum}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + tage);
    const neu = d.toISOString().slice(0, 10);
    if (istZeitraum) {
      const e = new Date(`${bis}T12:00:00Z`);
      e.setUTCDate(e.getUTCDate() + tage);
      setzeFelder({ datum: neu, bis: e.toISOString().slice(0, 10) });
    } else {
      setzeFelder({ datum: neu });
    }
  };

  const zeitraumUmschalten = () => {
    if (istZeitraum) {
      setzeFelder({ datum });
    } else {
      const ende = new Date(`${datum}T12:00:00Z`);
      ende.setUTCDate(ende.getUTCDate() + 6);
      const bisNeu = ende.toISOString().slice(0, 10);
      setzeFelder({ datum, bis: bisNeu > heute() ? heute() : bisNeu });
    }
  };

  const spurDaten = useMemo(() => tag?.spur ?? [], [tag]);
  const verlaufDaten = useMemo(() => tag?.verlauf ?? [], [tag]);

  const aufenthalte = tag?.verlauf.filter((e) => e.art === "visit") ?? [];
  const wege = tag?.verlauf.filter((e) => e.art === "activity") ?? [];
  const kilometer = wege.reduce((s, w) => s + (w.distanzM ?? 0), 0) / 1000;
  const fremdpunkteZahl = fremdspuren.reduce((s, f) => s + f.punkte.length, 0);

  // Google-Segmente und Fremdpunkte zu einer zeitlich sortierten Liste
  // verweben. Segmente sortieren nach ihrem Beginn, Fremdpunkte nach ihrem
  // Zeitstempel — beide in UTC-Millisekunden, damit die Reihenfolge stimmt.
  const zeilen = useMemo<Listenzeile[]>(() => {
    const segmente: Listenzeile[] = (tag?.verlauf ?? []).map((e) => ({
      typ: "segment" as const,
      zeit: Date.parse(e.vonUtc),
      eintrag: e,
    }));
    const punkte: Listenzeile[] = fremdspuren.flatMap((s) =>
      s.punkte.map((p) => ({ typ: "position" as const, zeit: p.tMs, farbe: s.farbe, pos: p })),
    );
    return [...segmente, ...punkte].sort((a, b) => a.zeit - b.zeit);
  }, [tag, fremdspuren]);

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-border bg-surface px-4 py-3 pl-16 lg:pl-4">
        <div className="flex items-center gap-1">
          <button
            onClick={() => verschieben(-1)}
            className="rounded-lg p-2 hover:bg-page-bg"
            aria-label={istZeitraum ? "Zeitraum zurück" : "Vorheriger Tag"}
          >
            <ChevronLeft size={18} />
          </button>
          <input
            type="date"
            value={datum}
            max={istZeitraum ? bis : heute()}
            onChange={(e) => e.target.value && setzeFelder({ datum: e.target.value, ...(istZeitraum ? { bis } : {}) })}
            className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm"
          />
          {istZeitraum && (
            <>
              <span className="text-text-muted">–</span>
              <input
                type="date"
                value={bis}
                min={datum}
                max={heute()}
                onChange={(e) => e.target.value && setzeFelder({ datum, bis: e.target.value })}
                className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm"
              />
            </>
          )}
          <button
            onClick={() => verschieben(1)}
            disabled={istZeitraum ? bis >= heute() : datum >= heute()}
            className="rounded-lg p-2 hover:bg-page-bg disabled:opacity-30"
            aria-label={istZeitraum ? "Zeitraum vor" : "Nächster Tag"}
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {!istZeitraum && (
          <button
            onClick={() => setzeFelder({ datum: heute() })}
            className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-page-bg"
          >
            Heute
          </button>
        )}

        <button
          onClick={zeitraumUmschalten}
          className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm hover:bg-page-bg ${
            istZeitraum ? "border-primary text-primary" : "border-border"
          }`}
          title={istZeitraum ? "Einzelnen Tag anzeigen" : "Zeitraum wählen"}
        >
          {istZeitraum ? <CalendarDays size={15} /> : <CalendarRange size={15} />}
          {istZeitraum ? "Ein Tag" : "Zeitraum"}
        </button>

        <QuellenAuswahl quellen={quellen} gewaehlt={gewaehlteQuellen} beiWechsel={setzeQuellen} />

        {(aufenthalte.length > 0 || wege.length > 0 || fremdpunkteZahl > 0) && (
          <div className="ml-auto flex items-center gap-4 text-sm text-text-muted">
            {aufenthalte.length > 0 && (
              <span className="flex items-center gap-1.5">
                <MapPin size={15} /> {aufenthalte.length} Aufenthalte
              </span>
            )}
            {wege.length > 0 && (
              <span className="flex items-center gap-1.5">
                <Footprints size={15} /> {wege.length} Wege
              </span>
            )}
            {kilometer > 0 && <span>{kilometer.toFixed(1)} km</span>}
            {fremdpunkteZahl > 0 && (
              <span className="flex items-center gap-1.5">
                <Radio size={15} /> {fremdpunkteZahl.toLocaleString("de-DE")} Punkte
              </span>
            )}
          </div>
        )}
      </header>

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <aside ref={listeRef} className="max-h-72 shrink-0 overflow-y-auto border-b border-border bg-surface lg:max-h-none lg:w-96 lg:border-r lg:border-b-0">
          {laedt && <Hinweis text="wird geladen…" />}
          {fehler && <Hinweis text={fehler} />}
          {!laedt && !fehler && zeilen.length === 0 && (
            <Hinweis
              text={
                gewaehlteQuellen.size === 0
                  ? "Keine Quelle gewählt."
                  : istZeitraum
                    ? "In diesem Zeitraum ist nichts aufgezeichnet."
                    : "Für diesen Tag ist nichts aufgezeichnet."
              }
            />
          )}

          <ol>
            {zeilen.map((zeile) =>
              zeile.typ === "segment" ? (
                <li key={`s${zeile.eintrag.id}`} data-id={zeile.eintrag.id}>
                  {bearbeitung === zeile.eintrag.id ? (
                    <SegmentBearbeiten
                      eintrag={zeile.eintrag}
                      beiSpeichern={segmentAktualisiert}
                      beiAbbrechen={() => setBearbeitung(null)}
                    />
                  ) : (
                    <div
                      className={`group flex w-full gap-3 border-b border-border px-4 py-3 text-left transition-colors hover:bg-page-bg ${
                        zeile.eintrag.id === gewaehlt ? "bg-page-bg" : ""
                      }`}
                    >
                      <button
                        onClick={() => waehlen(zeile.eintrag.id)}
                        className="flex min-w-0 flex-1 gap-3"
                      >
                        <Zeitspalte eintrag={zeile.eintrag} mitDatum={istZeitraum} />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5 text-sm">
                            {zeile.eintrag.art === "visit" ? (
                              <MapPin size={14} className="shrink-0 text-primary" />
                            ) : (
                              <Footprints size={14} className="shrink-0 text-text-muted" />
                            )}
                            <span className="truncate">{zeile.eintrag.beschriftung}</span>
                          </span>
                          <span className="mt-0.5 flex items-center gap-2 text-xs text-text-muted">
                            {zeile.eintrag.distanzM !== null && (
                              <span>{(zeile.eintrag.distanzM / 1000).toFixed(1)} km</span>
                            )}
                            {zeile.eintrag.semantikTyp && zeile.eintrag.semantikTyp !== "Unknown" && (
                              <span>{zeile.eintrag.semantikTyp}</span>
                            )}
                            {zeile.eintrag.abgeleiteteZeit && (
                              <span
                                className="flex items-center gap-1"
                                title="Google nennt für dieses Segment keine Zeitzone. Die Ortszeit wurde aus dem zeitlich nächsten Segment abgeleitet."
                              >
                                <Clock size={11} /> Zeit abgeleitet
                              </span>
                            )}
                          </span>
                        </span>
                      </button>
                      <button
                        onClick={() => setBearbeitung(zeile.eintrag.id)}
                        className="shrink-0 self-center rounded p-1.5 text-text-muted opacity-0 transition-opacity hover:bg-surface hover:text-text group-hover:opacity-100"
                        title="Eintrag bearbeiten"
                      >
                        <Pencil size={14} />
                      </button>
                      {segmentKoordinate(zeile.eintrag) && (
                        <a
                          href={googleMapsUrl(...segmentKoordinate(zeile.eintrag)!)}
                          target="_blank"
                          rel="noopener"
                          className="shrink-0 self-center rounded p-1.5 text-text-muted opacity-0 transition-opacity hover:bg-surface hover:text-primary group-hover:opacity-100"
                          title="In Google Maps öffnen"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                  )}
                </li>
              ) : (
                <li key={`p${zeile.pos.id}`}>
                  <FremdpunktZeile
                    pos={zeile.pos}
                    farbe={zeile.farbe}
                    mitDatum={istZeitraum}
                    beiKlick={() => fremdpunktWaehlen(zeile.pos)}
                  />
                </li>
              ),
            )}
          </ol>
        </aside>

        <div className="min-h-64 flex-1">
          <Suspense fallback={<Hinweis text="Karte wird geladen…" />}>
            <Kartenansicht
              spur={spurDaten}
              verlauf={verlaufDaten}
              fremdspuren={fremdspuren}
              hervorgehoben={gewaehlt}
              zentrum={zentrum}
              beiAuswahl={waehlen}
            />
          </Suspense>
        </div>
      </div>
    </div>
  );
}

function Hinweis({ text }: { text: string }) {
  return <p className="px-4 py-6 text-sm text-text-muted">{text}</p>;
}

/** Meter lesbar: „20 m", „2,4 km". */
function lesbareMeter(meter: number): string {
  if (meter >= 1000) return `${(meter / 1000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} km`;
  return `${Math.round(meter)} m`;
}

/**
 * Die Koordinate eines Segments für den Kartendienst — [lat, lon].
 * Ein Aufenthalt trägt sie direkt, ein Weg nimmt seinen Anfang.
 */
function segmentKoordinate(e: Verlaufseintrag): [number, number] | null {
  if (e.lat != null && e.lon != null) return [e.lat, e.lon];
  if (e.startLat != null && e.startLon != null) return [e.startLat, e.startLon];
  return null;
}

/**
 * Eine Zeile für einen Fremdpunkt (BMW, iPhone, Find My, Freund, AirTag).
 *
 * Schlichter als ein Google-Segment: ein Zeitpunkt, kein Zeitraum, kein
 * Bearbeiten. Der farbige Punkt links greift die Quellfarbe der Karte auf.
 */
function FremdpunktZeile({
  pos,
  farbe,
  mitDatum,
  beiKlick,
}: {
  pos: PositionEintrag;
  farbe: string;
  mitDatum: boolean;
  beiKlick: () => void;
}) {
  const batterie = typeof pos.meta?.batterie === "number" ? pos.meta.batterie : null;
  const anz = ortungAnzeige(ortungAus(pos.meta));

  return (
    <div className="group flex w-full gap-3 border-b border-border px-4 py-2.5 text-left transition-colors hover:bg-page-bg">
      <button onClick={beiKlick} className="flex min-w-0 flex-1 gap-3 text-left">
        <span
          className={`${mitDatum ? "w-40" : "w-28"} shrink-0 font-mono text-xs text-text-muted`}
        >
          {mitDatum ? tagUndUhrzeitLokal(pos.zeitpunkt) : uhrzeitLokal(pos.zeitpunkt)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 text-sm">
            <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: farbe }} />
            <span className="truncate">{pos.quelleName}</span>
          </span>
          <span className="mt-0.5 flex items-center gap-2 text-xs text-text-muted">
            {anz && (
              <span style={{ color: anz.farbe }} title={anz.erklaerung}>
                {anz.etikett}
                {pos.genauigkeit != null && pos.genauigkeit > 0 && (
                  <span className="opacity-70"> ±{lesbareMeter(pos.genauigkeit)}</span>
                )}
              </span>
            )}
            {!anz && pos.genauigkeit != null && pos.genauigkeit > 0 && (
              <span title="Genauigkeit">±{lesbareMeter(pos.genauigkeit)}</span>
            )}
            {batterie != null && <span title="Batterie">{batterie} %</span>}
          </span>
        </span>
      </button>
      <a
        href={googleMapsUrl(pos.lat, pos.lon)}
        target="_blank"
        rel="noopener"
        onClick={(e) => e.stopPropagation()}
        className="shrink-0 self-center rounded p-1.5 text-text-muted opacity-0 transition-opacity hover:bg-surface hover:text-primary group-hover:opacity-100"
        title="In Google Maps öffnen"
      >
        <ExternalLink size={14} />
      </a>
    </div>
  );
}

/**
 * Die Zeitspalte eines Eintrags.
 *
 * Oben steht die Zeit hier — die des Betrachters. Lag der Ort in einer anderen
 * Zeitzone, kommt die Ortszeit klein darunter; sonst bliebe unklar, warum ein
 * Abendessen mittags erscheint. Bei gleicher Zeitzone steht dort nichts, denn
 * „14:30 (14:30)" sagt niemandem etwas.
 */
function Zeitspalte({ eintrag, mitDatum }: { eintrag: Verlaufseintrag; mitDatum: boolean }) {
  const vonOrt = ortszeitFallsAbweichend(eintrag.vonUtc, eintrag.offsetMin);
  const bisOrt = ortszeitFallsAbweichend(eintrag.bisUtc, eintrag.offsetMin);
  const fremd = vonOrt !== null;

  const zeile = (iso: string, ortszeit: string | null) => (
    <>
      {mitDatum ? tagUndUhrzeitLokal(iso) : uhrzeitLokal(iso)}
      {ortszeit && <span className="text-text-muted/60"> ({ortszeit})</span>}
    </>
  );

  return (
    <span
      className={`${mitDatum ? (fremd ? "w-40" : "w-28") : fremd ? "w-32" : "w-24"} shrink-0 font-mono text-xs text-text-muted`}
      title={
        fremd
          ? `Zeiten in Ihrer Zeitzone; in Klammern die Ortszeit (${versatzText(eintrag.offsetMin)})`
          : undefined
      }
    >
      {zeile(eintrag.vonUtc, vonOrt)}
      <br />
      {zeile(eintrag.bisUtc, bisOrt)}
    </span>
  );
}
